/**
 * Funciones auxiliares para el cálculo de resúmenes de estadías
 * Incluye cálculos de tiempos de motor apagado/encendido y resúmenes totales
 */

/**
 * Calcula los tiempos de motor apagado y encendido para una estadía específica
 * @param {Array} registros - Array de registros ordenados por fecha
 * @param {Date} fechaInicio - Fecha de inicio de la estadía
 * @param {Date} fechaFin - Fecha de fin de la estadía
 * @param {string} tipoReporte - Tipo de reporte ('Detenidos', 'A/E', 'Entrada/Salida', etc.)
 * @returns {Object} - Objeto con tiempo_apagado y tiempo_encendido en segundos
 */
export const calcularTiemposMotorEstadia = (registros, fechaInicio, fechaFin, tipoReporte = '') => {
  if (!registros || registros.length === 0) {
    return {
      tiempo_apagado: 0,
      tiempo_encendido: 0
    };
  }

  // Filtrar registros dentro del rango de la estadía
  const registrosFiltrados = registros.filter(registro => {
    const fechaRegistro = new Date(registro.fecha);
    return fechaRegistro >= fechaInicio && fechaRegistro <= fechaFin;
  });

  if (registrosFiltrados.length === 0) {
    return {
      tiempo_apagado: 0,
      tiempo_encendido: 0
    };
  }

  // Ordenar por fecha para asegurar el orden correcto
  registrosFiltrados.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

  let tiempo_apagado = 0;
  let tiempo_encendido = 0;

  // Analizar intervalos entre registros consecutivos
  for (let i = 0; i < registrosFiltrados.length - 1; i++) {
    const registroActual = registrosFiltrados[i];
    const registroSiguiente = registrosFiltrados[i + 1];

    const fechaActual = new Date(registroActual.fecha);
    const fechaSiguiente = new Date(registroSiguiente.fecha);
    const duracionIntervalo = Math.floor((fechaSiguiente - fechaActual) / 1000);

    // Determinar el estado del motor para este intervalo
    // CORRECCIÓN: Considerar eventos de transición del registro siguiente SOLO para reportes de "Detenidos"
    const esReporteDetenidos = tipoReporte && (tipoReporte.includes('Detenidos') || tipoReporte.includes('DETENIDO'));
    
    let esMotorEncendido;
    
    if (esReporteDetenidos) {
      // ✅ LÓGICA ESPECIAL PARA REPORTES DE DETENIDOS
      const esEventoApagadoSiguiente = registroSiguiente.nombreEvento && 
                                     (registroSiguiente.nombreEvento.toLowerCase().includes('apagado de motor') ||
                                      registroSiguiente.nombreEvento.toLowerCase().includes('apagado'));

      const esEventoEncendidoSiguiente = registroSiguiente.nombreEvento && 
                                       registroSiguiente.nombreEvento.toLowerCase().includes('encendido');

      // Si el siguiente evento es "Apagado de Motor", el intervalo actual es motor ENCENDIDO
      // Si el siguiente evento es "Encendido", el intervalo actual es motor APAGADO
      if (esEventoApagadoSiguiente) {
        esMotorEncendido = true; // El intervalo antes de "Apagado" es motor encendido
        console.log(`  🟡 INTERVALO ANTES DE APAGADO (DETENIDOS): Motor considerado ENCENDIDO hasta evento "${registroSiguiente.nombreEvento}" en ${registroSiguiente.fecha}`);
      } else if (esEventoEncendidoSiguiente) {
        esMotorEncendido = false; // El intervalo antes de "Encendido" es motor apagado
        console.log(`  🟡 INTERVALO ANTES DE ENCENDIDO (DETENIDOS): Motor considerado APAGADO hasta evento "${registroSiguiente.nombreEvento}" en ${registroSiguiente.fecha}`);
      } else {
        // Usar el estado del registro actual para otros casos
        const tipoMotor = registroActual.tipoMotor;
        esMotorEncendido = tipoMotor === 1 || 
                           registroActual.tipo === 6 || 
                           registroActual.tipo === 4 ||
                           (registroActual.nombreEvento && 
                            registroActual.nombreEvento.toLowerCase().includes('encendido'));
      }
    } else {
      // ✅ LÓGICA ESTÁNDAR PARA OTROS TIPOS DE REPORTE (E/S, A/E, etc.)
      const tipoMotor = registroActual.tipoMotor;
      esMotorEncendido = tipoMotor === 1 || 
                         registroActual.tipo === 6 || 
                         registroActual.tipo === 4 ||
                         (registroActual.nombreEvento && 
                          registroActual.nombreEvento.toLowerCase().includes('encendido'));
      
      console.log(`  🔧 LÓGICA ESTÁNDAR (${tipoReporte}): Motor ${esMotorEncendido ? 'ENCENDIDO' : 'APAGADO'} - Registro: ${registroActual.nombreEvento || 'N/A'} (tipo: ${registroActual.tipo}, tipoMotor: ${registroActual.tipoMotor})`);
    }

    if (esMotorEncendido) {
      tiempo_encendido += duracionIntervalo;
    } else {
      tiempo_apagado += duracionIntervalo;
      // ✅ LOG ESPECÍFICO PARA MOTOR APAGADO
      console.log(`  🔴 INTERVALO MOTOR APAGADO: ${Math.floor(duracionIntervalo/60)}:${String(duracionIntervalo%60).padStart(2,'0')} - Registro tipo: ${registroActual.tipo}, tipoMotor: ${registroActual.tipoMotor}, evento: "${registroActual.nombreEvento}", desde: ${registroActual.fecha}`);
    }
  }

  // Para el último intervalo, usar el tiempo restante hasta el final de la estadía
  if (registrosFiltrados.length > 0) {
    const ultimoRegistro = registrosFiltrados[registrosFiltrados.length - 1];
    const fechaUltimoRegistro = new Date(ultimoRegistro.fecha);
    const tiempoRestante = Math.floor((fechaFin - fechaUltimoRegistro) / 1000);

    if (tiempoRestante > 0) {
      const esReporteDetenidos = tipoReporte && (tipoReporte.includes('Detenidos') || tipoReporte.includes('DETENIDO'));
      let esMotorEncendido;
      
      if (esReporteDetenidos) {
        // ✅ LÓGICA ESPECIAL PARA REPORTES DE DETENIDOS
        const esEventoApagado = ultimoRegistro.nombreEvento && 
                              (ultimoRegistro.nombreEvento.toLowerCase().includes('apagado de motor') ||
                               ultimoRegistro.nombreEvento.toLowerCase().includes('apagado'));

        const esEventoEncendido = ultimoRegistro.nombreEvento && 
                                ultimoRegistro.nombreEvento.toLowerCase().includes('encendido');

        if (esEventoApagado) {
          esMotorEncendido = false; // Después de un evento "Apagado", el motor está apagado
          console.log(`  🟡 TIEMPO RESTANTE DESPUÉS DE APAGADO (DETENIDOS): Motor considerado APAGADO después de "${ultimoRegistro.nombreEvento}" en ${ultimoRegistro.fecha}`);
        } else if (esEventoEncendido) {
          esMotorEncendido = true; // Después de un evento "Encendido", el motor está encendido
          console.log(`  🟡 TIEMPO RESTANTE DESPUÉS DE ENCENDIDO (DETENIDOS): Motor considerado ENCENDIDO después de "${ultimoRegistro.nombreEvento}" en ${ultimoRegistro.fecha}`);
        } else {
          // Usar el estado del tipoMotor para otros casos
          esMotorEncendido = ultimoRegistro.tipoMotor === 1 || 
                            ultimoRegistro.tipo === 6 || 
                            ultimoRegistro.tipo === 4 ||
                            (ultimoRegistro.nombreEvento && 
                             ultimoRegistro.nombreEvento.toLowerCase().includes('encendido'));
        }
      } else {
        // ✅ LÓGICA ESTÁNDAR PARA OTROS TIPOS DE REPORTE
        esMotorEncendido = ultimoRegistro.tipoMotor === 1 || 
                          ultimoRegistro.tipo === 6 || 
                          ultimoRegistro.tipo === 4 ||
                          (ultimoRegistro.nombreEvento && 
                           ultimoRegistro.nombreEvento.toLowerCase().includes('encendido'));
        
        console.log(`  🔧 TIEMPO RESTANTE ESTÁNDAR (${tipoReporte}): Motor ${esMotorEncendido ? 'ENCENDIDO' : 'APAGADO'} - Último registro: ${ultimoRegistro.nombreEvento || 'N/A'}`);
      }

      if (esMotorEncendido) {
        tiempo_encendido += tiempoRestante;
      } else {
        tiempo_apagado += tiempoRestante;
        // ✅ LOG ESPECÍFICO PARA TIEMPO RESTANTE CON MOTOR APAGADO
        console.log(`  🔴 TIEMPO RESTANTE MOTOR APAGADO: ${Math.floor(tiempoRestante/60)}:${String(tiempoRestante%60).padStart(2,'0')} - Último registro tipo: ${ultimoRegistro.tipo}, tipoMotor: ${ultimoRegistro.tipoMotor}, evento: "${ultimoRegistro.nombreEvento}", desde: ${ultimoRegistro.fecha}`);
      }
    }
  }

  return {
    tiempo_apagado: Math.max(0, tiempo_apagado),
    tiempo_encendido: Math.max(0, tiempo_encendido)
  };
};

/**
 * Calcula el resumen total de tiempos de todas las estadías
 * @param {Array} estadias - Array de estadías procesadas
 * @returns {Object} - Resumen con totales de tiempo apagado, encendido y estadísticas generales
 */
export const calcularResumenTotalEstadias = (estadias) => {
  if (!estadias || estadias.length === 0) {
    return {
      total_estadias: 0,
      tiempo_total_apagado: 0,
      tiempo_total_encendido: 0,
      tiempo_total_estadias: 0,
      tiempo_promedio_estadia: 0,
      porcentaje_apagado: 0,
      porcentaje_encendido: 0,
      estadias_por_categoria: {},
      estadias_por_vehiculo: {}
    };
  }

  let tiempo_total_apagado = 0;
  let tiempo_total_encendido = 0;
  let tiempo_total_estadias = 0;
  const estadias_por_categoria = {};
  const estadias_por_vehiculo = {};

  // Procesar cada estadía
  estadias.forEach(estadia => {
    // Sumar tiempos de motor
    tiempo_total_apagado += estadia.tiempo_apagado || 0;
    tiempo_total_encendido += estadia.tiempo_encendido || 0;
    tiempo_total_estadias += estadia.tiempo_segundos || 0; // ✅ CORREGIDO: usar tiempo_segundos en lugar de tiempo

    // Contar por categoría
    const categoria = estadia.categoria || 'Sin categoría';
    if (!estadias_por_categoria[categoria]) {
      estadias_por_categoria[categoria] = {
        cantidad: 0,
        tiempo_total: 0,
        tiempo_apagado: 0,
        tiempo_encendido: 0
      };
    }
    estadias_por_categoria[categoria].cantidad++;
    estadias_por_categoria[categoria].tiempo_total += estadia.tiempo_segundos || 0; // ✅ CORREGIDO
    estadias_por_categoria[categoria].tiempo_apagado += estadia.tiempo_apagado || 0;
    estadias_por_categoria[categoria].tiempo_encendido += estadia.tiempo_encendido || 0;

    // Contar por vehículo
    const vehiculo = estadia.uid_vehiculo || 'Sin vehículo';
    if (!estadias_por_vehiculo[vehiculo]) {
      estadias_por_vehiculo[vehiculo] = {
        cantidad: 0,
        tiempo_total: 0,
        tiempo_apagado: 0,
        tiempo_encendido: 0,
        alias: estadia.alias || vehiculo // ✅ CORREGIDO: usar alias en lugar de alias_vehiculo
      };
    }
    estadias_por_vehiculo[vehiculo].cantidad++;
    estadias_por_vehiculo[vehiculo].tiempo_total += estadia.tiempo_segundos || 0; // ✅ CORREGIDO
    estadias_por_vehiculo[vehiculo].tiempo_apagado += estadia.tiempo_apagado || 0;
    estadias_por_vehiculo[vehiculo].tiempo_encendido += estadia.tiempo_encendido || 0;
  });

  // Calcular estadísticas derivadas
  const tiempo_promedio_estadia = estadias.length > 0 ? 
    Math.round(tiempo_total_estadias / estadias.length) : 0;

  const tiempo_total_motor = tiempo_total_apagado + tiempo_total_encendido;
  const porcentaje_apagado = tiempo_total_motor > 0 ? 
    Math.round((tiempo_total_apagado / tiempo_total_motor) * 100) : 0;
  const porcentaje_encendido = tiempo_total_motor > 0 ? 
    Math.round((tiempo_total_encendido / tiempo_total_motor) * 100) : 0;

  return {
    total_estadias: estadias.length,
    tiempo_total_apagado,
    tiempo_total_encendido,
    tiempo_total_estadias,
    tiempo_promedio_estadia,
    porcentaje_apagado,
    porcentaje_encendido,
    estadias_por_categoria,
    estadias_por_vehiculo,
    tiempo_total_motor
  };
};

/**
 * Formatea tiempo en segundos a formato legible
 * @param {number} segundos - Tiempo en segundos
 * @returns {string} - Tiempo formateado (ej: "2h 30m 45s")
 */
export const formatearTiempoResumen = (segundos) => {
  if (!segundos || segundos <= 0) return "0s";

  const horas = Math.floor(segundos / 3600);
  const minutos = Math.floor((segundos % 3600) / 60);
  const segs = segundos % 60;

  let resultado = "";
  if (horas > 0) resultado += `${horas}h `;
  if (minutos > 0) resultado += `${minutos}m `;
  if (segs > 0 || resultado === "") resultado += `${segs}s`;

  return resultado.trim();
};

/**
 * Valida que una estadía tenga los campos necesarios para el cálculo
 * @param {Object} estadia - Objeto de estadía
 * @returns {boolean} - true si es válida, false si no
 */
export const validarEstadiaParaCalculo = (estadia) => {
  return estadia && 
         estadia.fecha_inicio && 
         estadia.fecha_fin && 
         typeof estadia.tiempo === 'number' && 
         estadia.tiempo > 0;
};
